import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { withToastManager } from 'react-toast-notifications';
import { Mutex } from 'async-mutex';
import { connect } from 'react-redux';
import memoize from 'memoize-one';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import api from '../api';
import {
  projectPagesActions, elementsActions, projectElementsActions,
  elementLinksActions, elementModalitiesActions,
  sortingHandlerActions, missingDataActions, projectsActions,
} from '../redux/actions';
import {
  getSortingHandlerId, getSortingItemId, extractSortingItemDigitalId,
} from '../redux/actions/sorting-handler';
import { nsOptions } from '../i18n';
import ElementUtil from '../utils/ElementUtil';
import Toast from '../utils/Toast';
import ElementAddBox from './ElementAddBox';
import { CardLoader } from './Loader';
import Help from './Help';
import FAQLink from './FAQLink';
import ErrorUtil from '../utils/ErrorUtil';
import { hasPermission } from '../utils/data-util';
import withLicenseMsgModal from './withLicenseMsgModal';
import DraggableComponent from './DraggableComponent';
import DroppableComponent from './DroppableComponent';
import FormTitle from './FormTitle';
import DropdownMenu from './NewDropdownMenu';
import DuplicationTreeViewModal from './DuplicationTreeViewModal';
import { NewBadge } from './Badges';
import {
  CAN_EDIT_FORM_AND_DOCUMENTATIONS, ELEMENT_TYPE_DIVIDER, ELEMENT_TYPE_MODULE,
  ELEMENT_TYPE_MULTIPLE_CHOICES, ELEMENT_TYPE_SPACER, ELEMENT_TYPE_TITLE,
  ELEMENT_TYPE_UNIQUE_CHOICE,
} from '../constants';
import { getRootProjectElements } from '../utils/links';
import fromReduxState from '../utils/redux';
import downloadEndpoint, { UTF8_BOM } from '../utils/downloadEndpoint';


const mapStateToProps = (state, ownProps) => ({
  user: state.auth.authUser,
  page: state.projectPages[ownProps.id],
  projectElements: state.projectElements,
  elements: state.elements,
  elementLinks: state.elementLinks,
  projectUser: Object.values(state.projectUsers).find((pUser) => (
    pUser.user && pUser.user.id === state.auth.authUser.id
      && pUser.project === ownProps.project.id
  )),
  sortedItems: state.sortingHandler[ownProps.formSortingHandlerId],
  modalOpened: state.dragAndDrop.modalsOpened > 0,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
  fetchPage: async () => dispatch(projectPagesActions.read(ownProps.id, {
    admin: ownProps.admin,
  })),
  addElement: async (data) => dispatch(elementsActions.create(data, {
    admin: ownProps.admin,
  })),
  resyncElements: async () => dispatch(elementsActions.resync({
    admin: ownProps.admin,
  })),
  addProjectElement: async (data) => dispatch(projectElementsActions.create(data, {
    admin: ownProps.admin,
  })),
  removeProjectElement: async (id) => dispatch(projectElementsActions.remove(id, {
    admin: ownProps.admin,
  })),
  resyncProjectElements: async () => dispatch(projectElementsActions.resync({
    admin: ownProps.admin,
  })),
  resyncElementLinks: async () => dispatch(elementLinksActions.resync({
    admin: ownProps.admin,
  })),
  addSortedItems: (items) => dispatch(sortingHandlerActions.addItems({
    handlerId: ownProps.formSortingHandlerId,
    items,
  })),
  removeSortedItem: (item) => dispatch(sortingHandlerActions.removeItem({
    handlerId: ownProps.formSortingHandlerId,
    item,
  })),
  fetchElement: async (id) => dispatch(elementsActions.read(id, {
    admin: ownProps.admin,
  })),
  fetchProjectElement: async (id) => dispatch(projectElementsActions.read(id, {
    admin: ownProps.admin,
  })),
  insertSortedItem: (previousItem, item) => dispatch(sortingHandlerActions.insertItem({
    handlerId: ownProps.formSortingHandlerId,
    previousItem,
    item,
  })),
  fetchElementModalities: async (elements) => dispatch(
    elementModalitiesActions.list({
      element__in: Object.values(elements).map((el) => el.id),
      admin: ownProps.admin,
    }),
  ),
  fetchElementLinks: async (projectElements) => dispatch(elementLinksActions.listByProjectElements(
    Object.values(projectElements).map((pEl) => pEl.id), { admin: ownProps.admin }, { pagination: 'no' },
  )),
  fetchProjectElements: async (pages) => dispatch(projectElementsActions.list({
    project_page__in: Object.values(pages).map((p) => p.key),
    admin: ownProps.admin,
  }, { pagination: 'no' })),
  fetchElements: async (projectElements) => dispatch(elementsActions.list({
    id__in: Object.values(projectElements).map((pEl) => pEl.element),
    admin: ownProps.admin,
  }, { pagination: 'no' })),
  notifyFormChange: async () => dispatch(missingDataActions.notifyFormChange(ownProps.project.id)),
  patchProject: async (data) => dispatch(projectsActions.patch(ownProps.project.id, data, {
    annotate_investigators_count: true,
    annotate_completion_rate: true,
  })),
});


@withToastManager
@connect(mapStateToProps, mapDispatchToProps, null, { forwardRef: true })
@withTranslation('', nsOptions)
@withLicenseMsgModal()
class ProjectForm extends Component {
  static propTypes = {
    t: PropTypes.func.isRequired,
    id: PropTypes.number.isRequired,
    formSortingHandlerId: PropTypes.string.isRequired,
    project: PropTypes.shape().isRequired,
    pages: PropTypes.arrayOf(PropTypes.shape()).isRequired,
    addTab: PropTypes.func.isRequired,
    cloneTab: PropTypes.func.isRequired,
    renameTab: PropTypes.func.isRequired,
    removeTab: PropTypes.func.isRequired,
    match: PropTypes.shape({
      url: PropTypes.string.isRequired,
      params: PropTypes.shape().isRequired,
    }).isRequired,
    location: PropTypes.shape().isRequired,
    admin: PropTypes.bool,
    editionMode: PropTypes.bool,
    onEditionToggle: PropTypes.func.isRequired,
    user: PropTypes.shape().isRequired,
    page: PropTypes.shape().isRequired,
    projectElements: PropTypes.shape().isRequired,
    elements: PropTypes.shape().isRequired,
    elementLinks: PropTypes.shape().isRequired,
    projectUser: PropTypes.shape(),
    sortedItems: PropTypes.arrayOf(PropTypes.string).isRequired,
    modalOpened: PropTypes.bool.isRequired,
    fetchPage: PropTypes.func.isRequired,
    addElement: PropTypes.func.isRequired,
    resyncElements: PropTypes.func.isRequired,
    addProjectElement: PropTypes.func.isRequired,
    removeProjectElement: PropTypes.func.isRequired,
    resyncProjectElements: PropTypes.func.isRequired,
    resyncElementLinks: PropTypes.func.isRequired,
    addSortedItems: PropTypes.func.isRequired,
    removeSortedItem: PropTypes.func.isRequired,
    fetchElement: PropTypes.func.isRequired,
    fetchProjectElement: PropTypes.func.isRequired,
    insertSortedItem: PropTypes.func.isRequired,
    fetchElementModalities: PropTypes.func.isRequired,
    fetchElementLinks: PropTypes.func.isRequired,
    fetchProjectElements: PropTypes.func.isRequired,
    fetchElements: PropTypes.func.isRequired,
    notifyFormChange: PropTypes.func.isRequired,
    patchProject: PropTypes.func.isRequired,
  };

  static defaultProps = {
    admin: false,
    editionMode: false,
    projectUser: undefined,
  };

  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      ready: false,
    };
    this.page = null;
    this.mounted = false;
    this.elementRefs = {};
    this.loadContent = memoize(this.load);
    this.mutex = new Mutex();
    this.toPElementsArray = fromReduxState((projectElements, id) => (
      Object.values(projectElements).filter((pEl) => pEl.project_page === id)
    ));
    this.toRootPElsArray = fromReduxState((projectElements, elementLinks) => getRootProjectElements(
      projectElements,
      Object.values(elementLinks),
    ));
  }

  componentDidMount() {
    this.mounted = true;
    this.initData();
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  initData = async () => {
    const {
      page, fetchPage, projectElements: pElements, elements, modalOpened, editionMode, id,
    } = this.props;
    const projectElements = this.toPElementsArray(pElements, id);

    if (!page) await fetchPage();

    this.setState({
      loading: false,
      ready: true,
    }, () => (
      this.load(editionMode, projectElements, elements, this.props.sortedItems, modalOpened)
    ));
  };

  addElement = async (data, tabId) => {
    this.setState({ loading: true });
    const release = await this.mutex.acquire();
    try {
      const {
        addElement, addProjectElement, addSortedItems, formSortingHandlerId, t, notifyFormChange,
      } = this.props;
      const newData = { ...data };
      if (ElementUtil.isElementStatic(data.type) && data.type !== ELEMENT_TYPE_TITLE) {
        newData.name = ElementUtil.getStaticElementTypeName(data.type, t);
      }
      const element = await addElement(newData);
      const res = await addProjectElement({ element: element.id, project_page: tabId });
      await addSortedItems([getSortingItemId(formSortingHandlerId, res.id)]);

      // Try to focus on the variable name
      if (this.elementRefs[res.id]) {
        const elBaseRef = this.elementRefs[res.id].elBaseRef
          || this.elementRefs[res.id].ref.elBaseRef;
        if (elBaseRef && elBaseRef.props && elBaseRef.props.element
          && elBaseRef.props.element.name === '') {
          elBaseRef.focusOnNameInput();
        }
      }
      Toast.success(this.props, 'error:valid.saved');
      notifyFormChange().catch((err) => {
        console.warn('Failed to notify form change after add:', err);
      });
    } catch (error) {
      ErrorUtil.handleCatched(this.props, error);
    } finally {
      release();
      this.setState({ loading: false });
    }
  };

  deleteElement = async (projectElement, formSortingHandlerId) => {
    this.setState({ loading: true });
    const {
      removeProjectElement, resyncElements, resyncProjectElements, removeSortedItem,
      resyncElementLinks, notifyFormChange,
    } = this.props;
    try {
      removeSortedItem(getSortingItemId(formSortingHandlerId, projectElement.id));
      await removeProjectElement(projectElement.id);
      await Promise.all([
        resyncElements(), // Associated element should have been deleted
        resyncProjectElements(), // Sorting value of other project elements may have changed
        resyncElementLinks(), // Links could have been removed in cascade
      ]);
      Toast.success(this.props, 'error:valid.saved');
      notifyFormChange().catch((err) => {
        console.warn('Failed to notify form change after delete:', err);
      });
    } catch (error) {
      ErrorUtil.handleCatched(this.props, error);
    } finally {
      this.setState({ loading: false });
    }
  };

  cloneElement = async (projectElement) => {
    this.setState({ loading: true });
    const release = await this.mutex.acquire();
    const {
      fetchElement, formSortingHandlerId, insertSortedItem, fetchElementLinks,
      fetchElementModalities, fetchProjectElements, fetchElements, admin, notifyFormChange,
    } = this.props;
    try {
      // Request cloning to the api
      const res = await api.create('clone-variable', {
        project_element: projectElement.id,
        related_objects: true,
      }, { admin });

      const clonedProjectElement = JSON.parse(res.project_element);
      const elementType = clonedProjectElement.element_type;
      const projectElements = await fetchProjectElements(this.props.pages);

      if (elementType === ELEMENT_TYPE_MODULE) {
        if (Object.keys(projectElements).length > 0) {
          const promRes = await Promise.all([
            fetchElements(projectElements),
            fetchElementLinks(projectElements),
          ]);
          const elements = promRes[0];
          await fetchElementModalities(elements);
        }
      } else {
        await fetchElement(clonedProjectElement.element);
        if ([ELEMENT_TYPE_MULTIPLE_CHOICES, ELEMENT_TYPE_UNIQUE_CHOICE].includes(elementType)) {
          await fetchElementModalities(this.props.elements);
        }
        await fetchElementLinks(projectElements);
      }

      const previousItem = getSortingItemId(formSortingHandlerId, projectElement.id);
      const item = getSortingItemId(formSortingHandlerId, clonedProjectElement.id);
      insertSortedItem(previousItem, item);

      Toast.success(this.props, 'error:valid.cloned');
      notifyFormChange().catch((err) => {
        console.warn('Failed to notify form change after clone:', err);
      });
    } catch (error) {
      ErrorUtil.handleCatched(this.props, error);
    } finally {
      release();
      this.setState({ loading: false });
    }
  };

  exportData = async () => {
    const { project, admin } = this.props;
    try {
      await downloadEndpoint(
        `projects/${project.id}/data-dictionary`,
        'get',
        { admin },
        undefined,
        UTF8_BOM,
      );
    } catch (error) {
      ErrorUtil.handleCatched(this.props, error, false);
    }
  };

  loadAllProjectElements = async () => {
    const { pages } = this.props;
    try {
      const pageIds = pages.map((p) => p.key).join(',');
      // Fetch all PE + elements directly, bypassing Redux list (which replaces state)
      const peData = await api.requestData(
        'project-elements', undefined, 'get',
        { project_page__in: pageIds, no_pagination: true },
      );
      const elementIds = [...new Set(peData.map((pe) => pe.element))].join(',');
      const elData = await api.requestData(
        'elements', undefined, 'get',
        { id__in: elementIds, no_pagination: true },
      );
      this.duplicationProjectElements = peData;
      this.duplicationElements = {};
      elData.forEach((el) => { this.duplicationElements[el.id] = el; });
      this.forceUpdate();
    } catch (error) {
      ErrorUtil.handleCatched(this.props, error);
    }
  };

  saveDuplicationConfig = async (selectedIds) => {
    const { t } = this.props;
    try {
      const allPEs = this.duplicationProjectElements
        || Object.values(this.props.projectElements);
      const patchPromises = allPEs.map((pe) => {
        const shouldBeDuplicable = selectedIds.includes(pe.id);
        if (pe.is_duplicable !== shouldBeDuplicable) {
          return api.request('project-elements', pe.id, 'patch', {}, {
            is_duplicable: shouldBeDuplicable,
          });
        }
        return null;
      }).filter(Boolean);

      await Promise.all(patchPromises);

      const allowDuplication = selectedIds.length > 0;
      await this.props.patchProject({
        allow_inclusion_duplication: allowDuplication,
      });

      // Update local data so next open reflects saved state
      if (this.duplicationProjectElements) {
        this.duplicationProjectElements = this.duplicationProjectElements.map(
          (pe) => ({ ...pe, is_duplicable: selectedIds.includes(pe.id) }),
        );
      }
      this.forceUpdate();
      Toast.success(this.props, t('project:duplication.save-success'));
    } catch (error) {
      ErrorUtil.handleCatched(this.props, error);
    }
  };

  load = (editionMode, projectElements, elements, sortedItems, modalOpened) => {
    const {
      project, formSortingHandlerId, projectElements: pElements, elementLinks,
    } = this.props;
    const rootProjectElements = this.toRootPElsArray(pElements, elementLinks);
    const tabId = this.props.id;
    this.elementRefs = {};
    const getElement = (index, Type, pElement, ref = null) => {
      const { id } = pElement;
      const sortingItemId = getSortingItemId(formSortingHandlerId, pElement.id);
      return Type ? (
        <DraggableComponent
          key={sortingItemId}
          draggableId={sortingItemId}
          droppableId={formSortingHandlerId}
          index={index}
          isDragDisabled={modalOpened || !editionMode}
        >
          <Type
            {...this.props}
            key={id}
            isEditMode={editionMode}
            isReadOnly={!editionMode}
            elementId={pElement.element}
            rootProjectElements={rootProjectElements}
            projectElementId={id}
            project={project}
            moduleInstanceId={null}
            parent={project}
            formSortingHandlerId={formSortingHandlerId}
            elementSortingHandlerId={getSortingHandlerId('element', id)}
            methods={{
              reload: () => (
                this.load(editionMode, projectElements, elements, sortedItems, modalOpened)
              ),
              remove: () => this.deleteElement(pElement, formSortingHandlerId),
              clone: () => this.cloneElement(pElement),
            }}
            tabId={tabId}
            ref={ref}
          />
        </DraggableComponent>
      ) : null;
    };

    const content = sortedItems.map((pElId, index) => {
      const dId = extractSortingItemDigitalId(pElId);
      const pElement = projectElements.find((pEl) => pEl.id === dId);
      if (pElement) {
        const { id, element: elementId } = pElement;
        const element = elements[elementId];
        const Type = ElementUtil.getElementType(element);
        const elementType = element.type;
        const refClbk = ![ELEMENT_TYPE_SPACER, ELEMENT_TYPE_DIVIDER].includes(elementType)
          ? (ref) => { this.elementRefs[id] = ref; } : null;
        return getElement(index, Type, pElement, refClbk);
      }
      // May happen when moving an element to another page and opening that page before
      // project elments have been resynchronized
      return null;
    });

    return content;
  };

  render() {
    const {
      t, pages, projectElements: pElements, elements, sortedItems, formSortingHandlerId,
      modalOpened, addTab, renameTab, removeTab, cloneTab, id, admin, match, projectUser,
      onEditionToggle, editionMode, project,
    } = this.props;
    const projectElements = this.toPElementsArray(pElements, id);
    const { ready, loading } = this.state;
    const content = ready ? this.loadContent(
      editionMode,
      projectElements,
      elements,
      sortedItems,
      modalOpened,
    ) : null;
    const canEdit = admin || hasPermission(projectUser, CAN_EDIT_FORM_AND_DOCUMENTATIONS);

    return ready ? (
      <div className="position-relative">
        <div className="row mb-3 align-items-center">
          <div className="col-auto">
            <h4 className="font-weight-normal mb-0 mr-2">
              {t('project:nav.form')}
            </h4>
          </div>
          <div
            className="col-auto px-0"
            style={{ fontSize: '1rem', paddingTop: '0.15rem' }}
          >
            <Help interactive>
              <FAQLink text="common:discover-features" />
            </Help>
          </div>
          <div className="col-auto px-0 ml-2 d-flex align-items-center">
            <DropdownMenu
              mainClass="d-inline-block actions-menu"
              type="dropdown"
              triggerElement={(
                <span className="px-3 cursor-pointer">
                  <NewBadge className="form-menu-badge">
                    <FontAwesomeIcon
                      icon={['fal', 'ellipsis-h']}
                      className="text-gray-dark"
                      size="2x"
                    />
                  </NewBadge>
                </span>
              )}
            >
              <button
                type="button"
                className="no-button-style dropdown-item"
                onClick={this.exportData}
              >
                <FontAwesomeIcon icon={['fas', 'book']} className="mr-2" />
                {t('project:export-data')}
              </button>
              {canEdit && (
                <DuplicationTreeViewModal
                  projectPages={pages}
                  projectElements={
                    this.duplicationProjectElements || Object.values(projectElements)
                  }
                  elements={
                    this.duplicationElements || elements
                  }
                  initialSelectedIds={
                    (this.duplicationProjectElements || Object.values(projectElements))
                      .filter((pe) => pe.is_duplicable)
                      .map((pe) => pe.id)
                  }
                  onLoad={this.loadAllProjectElements}
                  onSave={this.saveDuplicationConfig}
                >
                  <button
                    type="button"
                    className="no-button-style dropdown-item"
                  >
                    <FontAwesomeIcon
                      icon={project.allow_inclusion_duplication
                        ? ['fas', 'clone'] : ['far', 'clone']}
                      className={`mr-2${project.allow_inclusion_duplication
                        ? ' text-newyellow-1-dark' : ''}`}
                    />
                    <span>
                      {project.allow_inclusion_duplication
                        ? t('project:duplication.enabled-label')
                        : t('project:duplication.enable-button')}
                    </span>
                    <Help tooltipPlacement="bottom" iconClassName="ml-2" interactive>
                      {t('project:duplication.enable-tooltip')}
                      <br />
                      <FAQLink
                        articleId={16}
                        text="project:duplication.help-link"
                        linkClassName="classical-link text-newblue-1"
                      />
                    </Help>
                    <span
                      className="MuiBadge-badge ml-2"
                      style={{
                        position: 'relative',
                        transform: 'none',
                        display: 'inline-block',
                        backgroundColor: '#0051FF',
                        color: 'white',
                        fontSize: '10px',
                        verticalAlign: 'middle',
                      }}
                    >
                      {t('common:new')}
                    </span>
                  </button>
                </DuplicationTreeViewModal>
              )}
            </DropdownMenu>
          </div>
        </div>
        <div
          key={`content-${id}`}
          className="element-tab mt-4 position-relative"
          id={id}
        >
          { loading && <CardLoader /> }
          <div className="row justify-content-center align-items-center">
            <div className="col">
              <FormTitle
                admin={admin}
                addTab={addTab}
                editLinks={() => {}}
                renameTab={renameTab}
                removeTab={removeTab}
                cloneTab={cloneTab}
                editionMode={editionMode}
                showEditionBtn={canEdit}
                onEditionToggle={onEditionToggle}
                currentPageId={id}
                pages={pages}
                match={match}
                project={project}
              />
            </div>
          </div>
          <div className="row justify-content-center form-elements">
            <div className="col-12">
              <div className={`element-edit-form mt-0 ${editionMode ? '' : 'read-only-form'}`}>
                <DroppableComponent
                  droppableId={formSortingHandlerId}
                  type="FORM"
                  isDropDisabled={modalOpened}
                >
                  {content}
                </DroppableComponent>
              </div>
            </div>
            <div className="col-12 col-lg-11 col-xl-10 mt-4">
              {
                editionMode && (
                  <ElementAddBox
                    addElement={(data) => this.addElement(data, id,
                      () => (
                        this.loadContent(
                          editionMode,
                          projectElements,
                          elements,
                          sortedItems,
                          modalOpened,
                        )
                      ))}
                  />
                )
              }
            </div>
          </div>
        </div>
      </div>
    ) : (<CardLoader />);
  }
}


export default ProjectForm;
